import { makeStyles } from '@material-ui/core'
import React from 'react'
import { useTranslation } from 'react-i18next'
import CategoryItems from './CategoryItems'
import GuidelineCategory from './GuidelineCategory'
import ElasticsSetupAction from 'components/Patients/PatientSwitchableView/PatientCopilot/CopilotSettings/ElasticsSetupAction'
import { GuidelineCategories } from 'consts/hiToolsConsts'

const useStyles = makeStyles(theme => ({
  root: {
    padding: 10,
    overflowY: 'auto'
  },
  lastUpdated: {
    paddingBottom: 15,
    color: 'var(--text-color-11)'
  },
  categoriesContainer: {
    display: 'flex',
    flexDirection: 'column'
  }
}))

/**
 * @param {Object}  props
 * @param {Array}   props.categories       Category descriptors (key, icon, title, component, …).
 * @param {Object}  props.categoriesData   Per-category data keyed by category key.
 * @param {Object}  [props.actions]        Passed through to `CategoryItems`.
 * @param {string}  [props.generalNote]    Optional general note appended after categories.
 * @param {boolean} [props.enableElasticsQuickView=false]
 *   When true (set by patient-brief consumers), the elastics category renders via
 *   `ElasticsSetupAction` instead of `CategoryItems` — adding the eye icon + the
 *   permission-gated `SetupElasticsPopup`. Off by default so the practice-guidelines
 *   tab (which shares this component but doesn't have patient-brief data to save back)
 *   is unaffected.
 */
const Categories = ({ categories, categoriesData, actions, generalNote, enableElasticsQuickView = false }) => {
  const classes = useStyles()
  const { t } = useTranslation()

  return (
    <div className={classes.categoriesContainer}>
      {categories.map((category, index) => {
        const isElasticsQuickView = enableElasticsQuickView && category.key === GuidelineCategories.Elastics
        const items = categoriesData?.[category.key]?.items
        const note = categoriesData?.[category.key]?.note

        return (
          <GuidelineCategory
            key={`category-${category.key}`}
            icon={category.icon}
            title={category.title}
            isFirstCategory={index === 0}
            isLastCategory={index === categories.length - 1 && !generalNote}
          >
            {isElasticsQuickView ? (
              <ElasticsSetupAction categoryData={{ component: category.component, data: { items, note } }} />
            ) : (
              <CategoryItems categoryComponent={category.component} items={items} actions={actions} note={note} />
            )}
          </GuidelineCategory>
        )
      })}
      {generalNote && (
        <GuidelineCategory isCollapsible isLastCategory title={t('dialogs.hiPatientGuidelinesViewer.generalNote')}>
          <span style={{ whiteSpace: 'pre-line' }}>{generalNote}</span>
        </GuidelineCategory>
      )}
    </div>
  )
}

export default Categories
